import PropTypes from 'prop-types'
import { Suspense } from 'react'
import { Helmet } from 'react-helmet'
import { Link, Navigate, useParams } from 'react-router-dom'

import { findProductUpdate, PRODUCT_UPDATES } from '../product-updates'

// Oeffentlich ohne Login (siehe publicRoutes), damit sich der Link in
// Newslettern und Mails an Kunden teilen laesst. Uebersicht und Seite sind
// auch die Grundlage der internen Updates (pages/internal-updates.js).

const FONTS =
  'https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500&display=swap'

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })

const Head = ({ title, description, noindex }) => (
  <Helmet>
    <html lang='de' />
    <title>{title}</title>
    <meta name='description' content={description} />
    <meta property='og:title' content={title} />
    <meta property='og:description' content={description} />
    {noindex && <meta name='robots' content='noindex, nofollow' />}
    <link rel='stylesheet' href={FONTS} />
  </Helmet>
)

Head.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  noindex: PropTypes.bool
}

Head.defaultProps = { noindex: false }

const UPDATE_SHAPE = PropTypes.shape({
  slug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  Component: PropTypes.elementType.isRequired
})

export function UpdatesOverview({
  updates,
  basePath,
  title,
  description,
  intro,
  empty,
  noindex
}) {
  return (
    <div className='min-h-screen bg-white px-5 py-20'>
      <Head
        title={`Aivy ${title}`}
        description={description}
        noindex={noindex}
      />
      <div className='mx-auto max-w-3xl'>
        <p className='text-lg font-medium text-blue-600'>Aivy</p>
        <h1 className='mt-3 text-5xl font-semibold tracking-tight text-gray-900'>
          {title}
        </h1>
        {intro && <p className='mt-4 max-w-prose text-gray-600'>{intro}</p>}
        {updates.length === 0 ? (
          <p className='mt-14 border-y border-gray-200 py-8 text-gray-600'>
            {empty}
          </p>
        ) : (
          <ul className='mt-14 divide-y divide-gray-200 border-y border-gray-200'>
            {updates.map(({ slug, date, title: updateTitle, summary }) => (
              <li key={slug}>
                <Link
                  to={`${basePath}/${slug}`}
                  className='group block py-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600'
                >
                  <time dateTime={date} className='text-sm text-gray-500'>
                    {formatDate(date)}
                  </time>
                  <h2 className='mt-2 text-2xl font-semibold text-gray-900 group-hover:text-blue-600'>
                    {updateTitle}
                  </h2>
                  <p className='mt-2 max-w-prose text-gray-600'>{summary}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

UpdatesOverview.propTypes = {
  updates: PropTypes.arrayOf(UPDATE_SHAPE).isRequired,
  basePath: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  intro: PropTypes.string,
  empty: PropTypes.string,
  noindex: PropTypes.bool
}

UpdatesOverview.defaultProps = { intro: '', empty: '', noindex: false }

export function UpdatePage({ update, basePath, suffix, noindex }) {
  if (!update) return <Navigate replace to={basePath} />

  const { Component, title, summary } = update

  return (
    <>
      <Head
        title={`${title} · Aivy ${suffix}`}
        description={summary}
        noindex={noindex}
      />
      <Suspense fallback={<div className='min-h-screen bg-white' />}>
        <Component />
      </Suspense>
    </>
  )
}

UpdatePage.propTypes = {
  update: UPDATE_SHAPE,
  basePath: PropTypes.string.isRequired,
  suffix: PropTypes.string.isRequired,
  noindex: PropTypes.bool
}

UpdatePage.defaultProps = { update: undefined, noindex: false }

export function ProductUpdatesOverview() {
  return (
    <UpdatesOverview
      updates={PRODUCT_UPDATES}
      basePath='/product/updates'
      title='Produkt-Updates'
      description='Neuerungen in der Aivy App und im Dashboard.'
    />
  )
}

export function ProductUpdate() {
  const { slug } = useParams()

  return (
    <UpdatePage
      update={findProductUpdate(slug)}
      basePath='/product/updates'
      suffix='Produkt-Updates'
    />
  )
}
